/**
 * The one nav model. Sidebar, collapsed flyout, breadcrumbs and the command
 * palette all read from `buildNav`, a pure function of
 * (role, route, counts) so it can be unit-tested per role.
 */
import type { Role } from "./app/session";

export type BadgeTone = "muted" | "warning" | "info" | "danger" | "success";

export type NavSub = {
  key: string;
  label: string;
  to: string;
  active: boolean;
  badge?: string;
  badgeTone?: BadgeTone;
};

export type NavSection = {
  key: string;
  label: string;
  icon: string;
  to: string;
  active: boolean;
  badge?: string;
  badgeTone?: BadgeTone;
  subs: NavSub[];
};

export type Crumb = { label: string; to?: string };

export type NavCounts = Partial<{
  emuMine: number;
  emuShared: number;
  emuAll: number;
  running: number;
  failedSnaps: number;
  members: number;
  groups: number;
  unlinkedMembers: number;
  infraUpdates: number;
  tenants: number;
  gitlabLinked: boolean;
}>;

export type NavInput = {
  role: Role;
  pathname: string;
  search: string;
  counts: NavCounts;
  /** The emulator open in the emulator view, if any. */
  emulator?: { id: string; name: string; tab: EmuTab; running: boolean };
  workspaces: "available" | "comingSoon" | "hidden";
  /** `features.toolDistribution`: adds Organization › Tools. */
  toolDistribution?: boolean;
  /** Tenant name when on a tenant detail route. */
  tenantName?: string;
};

export type EmuTab = "mine" | "shared" | "all";

export const ORG_TABS = [
  ["members", "Members"],
  ["groups", "Groups"],
  ["access", "Access"],
  ["integrations", "GitLab & invites"],
  ["infra", "Infrastructure"],
  ["tools", "Tools"],
] as const;
export type OrgTab = (typeof ORG_TABS)[number][0];

/** Tabs shown for the current feature set; `tools` is flag-gated. */
export const orgTabs = (toolDistribution?: boolean) =>
  ORG_TABS.filter(([k]) => k !== "tools" || toolDistribution);

export const ACCOUNT_TABS = [
  ["profile", "Profile"],
  ["accounts", "Connected accounts"],
  ["prefs", "Preferences"],
] as const;
export type AccountTab = (typeof ACCOUNT_TABS)[number][0];

const EMU_TABS: [EmuTab, string][] = [
  ["mine", "Mine"],
  ["shared", "Shared with me"],
  ["all", "All"],
];

const num = (n?: number) => (n == null ? undefined : String(n));

export function emuTabFrom(search: string, role: Role): EmuTab {
  const t = new URLSearchParams(search).get("tab");
  if (t === "shared") return "shared";
  if (t === "all" && role !== "developer") return "all";
  return "mine";
}

export function buildNav(input: NavInput): { sections: NavSection[]; bottom: NavSection[]; crumbs: Crumb[] } {
  const { role, pathname, search, counts, emulator, workspaces } = input;
  const q = new URLSearchParams(search);
  const isAdmin = role !== "developer";
  const isGlobal = role === "globalAdmin";
  const seg = pathname.split("/").filter(Boolean);
  const area = seg[0] ?? "home";

  const emuTab = emuTabFrom(search, role);
  const onEmuView = area === "emulators" && Boolean(seg[1]);
  const emuSubs: NavSub[] = EMU_TABS.filter(([k]) => k !== "all" || isAdmin).map(([k, label]) => ({
    key: k,
    label,
    to: `/emulators?tab=${k}`,
    active: area === "emulators" && !onEmuView && emuTab === k,
    badge: num(k === "mine" ? counts.emuMine : k === "shared" ? counts.emuShared : counts.emuAll),
  }));
  if (onEmuView && emulator) {
    emuSubs.push({
      key: "current",
      label: emulator.name,
      to: `/emulators/${emulator.id}`,
      active: true,
      badge: emulator.running ? "●" : undefined,
      badgeTone: "success",
    });
  }

  const wsSoon = workspaces === "comingSoon";
  const wsTab = emuTabFrom(search, role);

  const status = q.get("status");
  const roots = q.get("root") === "1";
  const bootable = q.get("bootable") === "1";

  const orgTab = area === "org" ? (seg[1] ?? "members") : undefined;
  const platTab = area === "platform" ? (seg[1] ?? "overview") : undefined;
  const acctTab = area === "account" ? (seg[1] ?? "profile") : undefined;

  const sections: NavSection[] = [
    { key: "home", label: "Home", icon: "home", to: "/", active: area === "home", subs: [] },
    {
      key: "emulators",
      label: "Emulators",
      icon: "devices",
      to: "/emulators",
      active: area === "emulators",
      badge: num(counts.running),
      subs: emuSubs,
    },
    ...(workspaces === "hidden"
      ? []
      : [
          {
            key: "workspaces",
            label: "Workspaces",
            icon: "code",
            to: "/workspaces",
            active: area === "workspaces",
            badge: wsSoon ? "Soon" : undefined,
            badgeTone: wsSoon ? ("info" as const) : undefined,
            subs: wsSoon
              ? []
              : EMU_TABS.filter(([k]) => k !== "all" || isAdmin).map(([k, label]) => ({
                  key: k,
                  label,
                  to: `/workspaces?tab=${k}`,
                  active: area === "workspaces" && wsTab === k,
                })),
          },
        ]),
    {
      key: "builds",
      label: "Builds",
      icon: "stack-2",
      to: "/builds",
      active: area === "builds",
      subs: [
        { key: "all", label: "All snapshots", to: "/builds", active: area === "builds" && !status && !roots && !bootable },
        { key: "bootable", label: "Bootable", to: "/builds?bootable=1", active: area === "builds" && bootable },
        {
          key: "failed",
          label: "Failed & canceled",
          to: "/builds?status=failed",
          active: area === "builds" && status === "failed",
          badge: counts.failedSnaps ? String(counts.failedSnaps) : undefined,
          badgeTone: "warning",
        },
        { key: "roots", label: "Roots", to: "/builds?root=1", active: area === "builds" && roots },
      ],
    },
    ...(isAdmin
      ? [
          {
            key: "org",
            label: "Organization",
            icon: "building",
            to: "/org/members",
            active: area === "org",
            subs: orgTabs(input.toolDistribution).map(([k, label]) => ({
              key: k,
              label,
              to: `/org/${k}`,
              active: orgTab === k,
              ...(k === "members"
                ? { badge: num(counts.members) }
                : k === "groups"
                  ? { badge: num(counts.groups) }
                  : k === "integrations" && counts.unlinkedMembers
                    ? { badge: String(counts.unlinkedMembers), badgeTone: "warning" as const }
                    : k === "infra" && counts.infraUpdates
                      ? { badge: String(counts.infraUpdates), badgeTone: "info" as const }
                      : {}),
            })),
          },
        ]
      : []),
    ...(isGlobal
      ? [
          {
            key: "platform",
            label: "Platform",
            icon: "server-2",
            to: "/platform/overview",
            active: area === "platform",
            subs: [
              { key: "overview", label: "Overview", to: "/platform/overview", active: platTab === "overview" },
              { key: "cluster", label: "Cluster", to: "/platform/cluster", active: platTab === "cluster" },
              { key: "settings", label: "Settings", to: "/platform/settings", active: platTab === "settings" },
              {
                key: "tenants",
                label: "Tenants",
                to: "/platform/tenants",
                active: platTab === "tenants",
                badge: num(counts.tenants),
              },
            ],
          },
        ]
      : []),
  ];

  const unlinked = counts.gitlabLinked === false;
  const bottom: NavSection[] = [
    // The CLI lives in one place, one click from everywhere.
    { key: "cli", label: "nullctl", icon: "terminal", to: "/cli", active: area === "cli", subs: [] },
    {
      key: "account",
      label: "Account",
      icon: "user-circle",
      to: "/account/profile",
      active: area === "account",
      badge: unlinked ? "!" : undefined,
      badgeTone: "warning",
      subs: ACCOUNT_TABS.map(([k, label]) => ({
        key: k,
        label,
        to: `/account/${k}`,
        active: acctTab === k,
        ...(k === "accounts" && unlinked ? { badge: "Link", badgeTone: "warning" as const } : {}),
      })),
    },
  ];

  return { sections, bottom, crumbs: buildCrumbs(input, { area, seg, emuTab, status, roots, bootable, orgTab, platTab, acctTab, wsSoon }) };
}

/** decodeURIComponent that keeps a malformed segment raw instead of throwing. */
function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function buildCrumbs(
  input: NavInput,
  c: {
    area: string;
    seg: string[];
    emuTab: EmuTab;
    status: string | null;
    roots: boolean;
    bootable: boolean;
    orgTab?: string;
    platTab?: string;
    acctTab?: string;
    wsSoon: boolean;
  },
): Crumb[] {
  const label = (list: readonly (readonly [string, string])[], k?: string) =>
    list.find(([key]) => key === k)?.[1] ?? list[0][1];
  const emuLabel = (t: EmuTab) => EMU_TABS.find(([k]) => k === t)![1];

  switch (c.area) {
    case "emulators":
      if (c.seg[1] && input.emulator) {
        const t = input.emulator.tab;
        return [
          { label: "Emulators", to: "/emulators" },
          { label: emuLabel(t), to: `/emulators?tab=${t}` },
          { label: input.emulator.name },
        ];
      }
      return [{ label: "Emulators", to: "/emulators" }, { label: emuLabel(c.emuTab) }];
    case "workspaces":
      return c.wsSoon
        ? [{ label: "Workspaces" }]
        : [{ label: "Workspaces", to: "/workspaces" }, { label: emuLabel(c.emuTab) }];
    case "builds":
      return [
        { label: "Builds", to: "/builds" },
        {
          label: c.roots
            ? "Roots"
            : c.bootable
              ? "Bootable"
              : c.status === "failed"
                ? "Failed & canceled"
                : c.status === "success"
                  ? "Successful"
                  : "All snapshots",
        },
      ];
    case "org":
      if (c.orgTab === "tools" && c.seg[2]) {
        return [
          { label: "Organization", to: "/org/members" },
          { label: "Tools", to: "/org/tools" },
          { label: safeDecode(c.seg[2]) },
        ];
      }
      return [{ label: "Organization", to: "/org/members" }, { label: label(ORG_TABS, c.orgTab) }];
    case "cli":
      return [{ label: "nullctl" }];
    case "account":
      return [{ label: "Account", to: "/account/profile" }, { label: label(ACCOUNT_TABS, c.acctTab) }];
    case "platform":
      if (c.platTab === "tenants") {
        const detail = c.seg[2];
        return [
          { label: "Platform", to: "/platform/overview" },
          { label: "Tenants", to: detail ? "/platform/tenants" : undefined },
          ...(detail === "new"
            ? [{ label: "Onboard tenant" }]
            : detail
              ? [{ label: input.tenantName ?? detail }]
              : []),
        ];
      }
      return [
        { label: "Platform", to: "/platform/overview" },
        { label: c.platTab === "cluster" ? "Cluster" : c.platTab === "settings" ? "Settings" : "Overview" },
      ];
    default:
      return [{ label: "Home" }];
  }
}
